import cloneDeep from 'lodash/cloneDeep';
import defaults from 'lodash/defaults';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import forEachDeep from '../../utils/forEachDeep';
import api from './api';

/**
 * Audio objects must be transformed to IDs. This array contains
 *  the pairs of object/id keys to be used.
 * The first string of each tuple is the key for the audio object, and the
 *  second is the key to be used to store the extracted ID.
 * @type {[string, string][]}
 */
const AudioPropertiesForTransform = [
    ['audio', 'audio_id'],
    ['final_audio', 'final_audio_id'],
];

/**
 * @param {Object} task TaskType-specific data.
 * @returns {Promise<unknown>} TaskType-specific response.
 * @see http://dev-dyl-website-docs-f1dd5d50.s3-website.eu-west-2.amazonaws.com/#api-Tasks-putTaskItems
 */
export default async function updateTaskItems(level_id, task) {
    task = cloneDeep(task);

    /* Convert all audio objects to their IDs
     * The task is wrapped in an array here so that the root object itself
     *  is also scanned.
     */
    forEachDeep([task], (value) => {
        if (!value || typeof value !== 'object') return;

        /* - Find objects with key `audioKey`
         * - Extract its audio_id value
         * - Add that audio_id to the parent object at `audioIdKey`
         * - Remove the object at `audioKey`
         */
        for (const [audioKey, audioIdKey] of AudioPropertiesForTransform) {
            if (audioKey in value) {
                if (value[audioKey]) {
                    value[audioIdKey] = value[audioKey]?.audio_id || null;
                } else {
                    value[audioIdKey] = null;
                }

                delete value[audioKey];
            }
        }
    });

    const body = pick(task, [
        'task_id_name',
        'background_image',
        'background_image_2',
        'background_image_3',
        'decorative_image',
        'decorative_image_2',
        'decorative_image_3',
        'decorative_image_4',
        'colors',
        'final_audio_id',
        // Backend seems to have started using `context_audio_id`,
        // But it may not be consistent, keeping `context_audio` field in to avoid regressions.
        'context_audio',
        'context_audio_id',
        'calibration_status',
        'anchor',
    ]);
    if (task.task_type_id === 'productive_writing') {
        body.is_question_changed = Boolean(task.is_question_changed);
    }

    body.items = task.items.map(item => {
        item = omit(item, [
            'rubric',
            'error',
            'order'
        ]);

        // Ensure correct answer order (some task types use different orders in their GET responses).
        item.answers.sort((a, b) => a.order - b.order);

        item.answers = item.answers.map(answer => {
            answer = omit(answer, [
                'option_order',
                'error',
            ]);

            switch (task.task_type_id) {
                case 'minimal_differences': {
                    if (!answer.text) {
                        answer.text = null;
                    }
                    break;
                }
            }

            return answer;
        });

        if (item.question_1?.length < 1) {
            item.question_1 = null;
        }

        if (item.question_2?.length < 1) {
            item.question_2 = null;
        }
        if (task.task_type_id === 'scene_creation' && item.question !== null) {
            if (item.question.trim() === '' || item.question === undefined) {
                item.question = null;
            }
        }
        if (item.backups) {
            // Set defaults for 'backup' objects
            item.backups = item.backups
                /* Remove backups without an audio - this safely
                 *   clears the object in the DB.
                 */
                .filter(backup => !!backup.audio_id)
                .map(backup => defaults(backup, {
                    backup_id: null,
                    audio_id: null,
                    delay: null,
                }));
        }

        return item;
    });

    /* Remove properties that should never be included, like 'order'.
     * Do this after other transformations, as some of these properties
     *  may be used in those transformations first.
     */
    forEachDeep([body], (value, key, parent, path) => {
        if (!value || typeof value !== 'object') return;

        // Remove all `order` properties, except the very specific cases...
        if ('order' in value) {
            let shouldNotRemoveOrder = (
                // Keep order in Factual Topic item objects
                task.task_type_id === 'factual_topic' && (
                    /items\[[0-9]+\]$/.test(path)
                )
            );

            if (!shouldNotRemoveOrder) {
                delete value.order;
            }
        }
    });

    const { data } = await api.put(
        `levels/${level_id}/task_types/${task.task_type_id}/tasks/${task.task_id}/items`,
        { ...body }
    );

    return data;
}